/**
 * Iter T · Phase 1 — Admin Quote Activity list.
 *
 * Q5.3 update — adds skip/offset pagination, date-range filter, and
 * CSV export. Server-side authoritative for every filter axis (search,
 * pipeline, sort, dates) — no client-side re-filtering. Recent Quote
 * Activity now shows "X–Y of Z" across the FULL matching set.
 *
 * Additive, read-only surface. Never modifies quote records except via
 * the "Mark as read" action inside QuoteDetail.
 */
import React, { useEffect, useState, useCallback, useMemo } from "react";
import { Loader2, ChevronRight, CheckCircle2, Download } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { queueTwoLine } from "@/lib/businessTime";
import { toast } from "sonner";


const STATUS_LABEL = {
  new:       { label: "New Quote",     tone: "bg-amber-100 border-amber-800 text-amber-900" },
  viewed:    { label: "Quote Viewed",  tone: "bg-neutral-100 border-neutral-500 text-neutral-700" },
  converted: { label: "Converted",     tone: "bg-emerald-100 border-emerald-800 text-emerald-900" },
};


// Q5.1 — derived sales-pipeline pill definitions. Kept in sync with
// backend `PIPELINE_*` constants in `routers/quotes_admin.py`.
const PIPELINE_LABEL = {
  new:               { label: "New",               tone: "bg-amber-100 border-amber-800 text-amber-900" },
  follow_up:         { label: "Follow-Up",         tone: "bg-sky-100 border-sky-800 text-sky-900" },
  recovery:          { label: "Recovery",          tone: "bg-orange-100 border-orange-800 text-orange-900" },
  order_started:     { label: "Order Started",     tone: "bg-indigo-100 border-indigo-800 text-indigo-900" },
  payment_submitted: { label: "Payment Submitted", tone: "bg-purple-100 border-purple-800 text-purple-900" },
  converted:         { label: "Converted",         tone: "bg-emerald-100 border-emerald-800 text-emerald-900" },
  lost_or_suppressed:{ label: "Lost / Suppressed", tone: "bg-neutral-200 border-neutral-600 text-neutral-800" },
};

const PIPELINE_FILTER_KEYS = [
  "new", "follow_up", "recovery", "order_started",
  "payment_submitted", "converted", "lost_or_suppressed",
];

// Q5.3 — date range presets. Resolved to concrete YYYY-MM-DD in
// America/New_York on submit (see `resolveDateRange`). "custom" opens
// two date inputs.
const DATE_RANGE_OPTIONS = [
  { value: "all",       label: "All Time" },
  { value: "today",     label: "Today" },
  { value: "yesterday", label: "Yesterday" },
  { value: "last_7",    label: "Last 7 Days" },
  { value: "last_30",   label: "Last 30 Days" },
  { value: "last_90",   label: "Last 90 Days" },
  { value: "custom",    label: "Custom Range" },
];

const PAGE_SIZE_OPTIONS = [25, 50, 100, 200];
const DEFAULT_PAGE_SIZE = 100;

// Compute the current ET calendar day as a YYYY-MM-DD string. Matches
// what the backend uses in `_parse_date_ymd_to_utc_iso`.
function etYmd(offsetDays = 0) {
  const now = new Date();
  // Use Intl to get the ET date parts.
  const parts = new Intl.DateTimeFormat("en-CA", {
    timeZone: "America/New_York",
    year: "numeric", month: "2-digit", day: "2-digit",
  }).formatToParts(now).reduce((acc, p) => (acc[p.type] = p.value, acc), {});
  const base = new Date(`${parts.year}-${parts.month}-${parts.day}T12:00:00Z`);
  base.setUTCDate(base.getUTCDate() + offsetDays);
  const y = base.getUTCFullYear();
  const m = String(base.getUTCMonth() + 1).padStart(2, "0");
  const d = String(base.getUTCDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

function resolveDateRange(preset, customStart, customEnd) {
  const today = etYmd(0);
  const yday  = etYmd(-1);
  switch (preset) {
    case "today":     return { start: today, end: today };
    case "yesterday": return { start: yday,  end: yday };
    case "last_7":    return { start: etYmd(-6),  end: today };
    case "last_30":   return { start: etYmd(-29), end: today };
    case "last_90":   return { start: etYmd(-89), end: today };
    case "custom":    return { start: customStart || "", end: customEnd || "" };
    case "all":
    default:          return { start: "", end: "" };
  }
}


function PipelinePill({ pipeline }) {
  const p = PIPELINE_LABEL[pipeline];
  if (!p) return <span className="italic text-xs opacity-60">—</span>;
  return (
    <span
      className={`inline-flex items-center gap-1 border-2 rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide ${p.tone}`}
      data-testid={`quote-pipeline-${pipeline}`}
    >
      {p.label}
    </span>
  );
}


const VARIANT_LABEL = {
  white:        { label: "WHITE",        tone: "bg-neutral-100 border-neutral-500 text-neutral-800" },
  blue:         { label: "BLUE",         tone: "bg-sky-100 border-sky-800 text-sky-900" },
  gold_crumple: { label: "GOLD CRUMPLE", tone: "bg-amber-100 border-amber-800 text-amber-900" },
  red:          { label: "RED",          tone: "bg-red-100 border-red-800 text-red-900" },
  orange:       { label: "ORANGE",       tone: "bg-orange-100 border-orange-800 text-orange-900" },
  purple:       { label: "PURPLE",       tone: "bg-purple-100 border-purple-800 text-purple-900" },
};


function VariantChip({ variant, publicId }) {
  const v = VARIANT_LABEL[variant];
  if (!v) {
    return (
      <span className="italic text-xs opacity-60" data-testid={`quote-variant-${publicId}`}>—</span>
    );
  }
  return (
    <span
      className={`inline-flex items-center gap-1 border-2 rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide ${v.tone}`}
      data-testid={`quote-variant-${publicId}`}
    >
      {v.label}
    </span>
  );
}


function StatusPill({ status, admin_read }) {
  const s = STATUS_LABEL[status] || STATUS_LABEL.new;
  return (
    <span
      className={`inline-flex items-center gap-1 border-2 rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide ${s.tone}`}
      data-testid={`quote-status-${status}`}
    >
      {!admin_read && status === "new" && (
        <span className="inline-block w-1.5 h-1.5 rounded-full bg-red-600" aria-label="Unread" />
      )}
      {s.label}
    </span>
  );
}


export default function QuoteList({ onOpen }) {
  const [rows, setRows] = useState(null);
  const [err, setErr] = useState(null);
  const [total, setTotal] = useState(0);

  // Filter axes
  const [pipeline, setPipeline] = useState(null);
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [sortBy, setSortBy] = useState("created_desc");

  // Q5.3 · Date range + pagination
  const [datePreset, setDatePreset] = useState("all");
  const [customStart, setCustomStart] = useState("");
  const [customEnd, setCustomEnd] = useState("");
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [offset, setOffset] = useState(0);
  const [exporting, setExporting] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search.trim()), 300);
    return () => clearTimeout(t);
  }, [search]);

  // Any change to filter axes / sort / page size resets pagination.
  useEffect(() => {
    setOffset(0);
  }, [pipeline, debouncedSearch, sortBy, datePreset, customStart, customEnd, pageSize]);

  // Build the current query params — memo so the export button uses
  // exactly the same shape as the list.
  const filterParams = useMemo(() => {
    const { start, end } = resolveDateRange(datePreset, customStart, customEnd);
    const p = new URLSearchParams();
    if (pipeline)                            p.set("pipeline", pipeline);
    if (debouncedSearch)                     p.set("q", debouncedSearch);
    if (sortBy && sortBy !== "created_desc") p.set("sort_by", sortBy);
    if (start)                               p.set("start_date", start);
    if (end)                                 p.set("end_date", end);
    return p;
  }, [pipeline, debouncedSearch, sortBy, datePreset, customStart, customEnd]);

  const load = useCallback(async () => {
    try {
      setErr(null);
      const params = new URLSearchParams(filterParams);
      params.set("limit", String(pageSize));
      params.set("offset", String(offset));
      const r = await api.get(`/admin/quotes?${params.toString()}`);
      setRows(r.data.quotes || []);
      setTotal(Number(r.data.total || 0));
    } catch (e) {
      setErr(formatApiError(e.response?.data?.detail) || e.message);
      setRows([]);
      setTotal(0);
    }
  }, [filterParams, pageSize, offset]);

  useEffect(() => { load(); }, [load]);

  const activeQuery =
    pipeline
    || debouncedSearch
    || sortBy !== "created_desc"
    || datePreset !== "all";

  const currentPage  = Math.floor(offset / pageSize) + 1;
  const totalPages   = Math.max(1, Math.ceil((total || 0) / pageSize));
  const showingStart = total === 0 ? 0 : offset + 1;
  const showingEnd   = Math.min(offset + pageSize, total);

  const goPrev = () => setOffset(Math.max(0, offset - pageSize));
  const goNext = () => setOffset(Math.min((totalPages - 1) * pageSize, offset + pageSize));
  const goFirst = () => setOffset(0);
  const goLast  = () => setOffset((totalPages - 1) * pageSize);

  const handleExport = async () => {
    try {
      setExporting(true);
      const params = new URLSearchParams(filterParams);
      const url = `/admin/quotes/export?${params.toString()}`;
      const r = await api.get(url, { responseType: "blob" });
      const blob = new Blob([r.data], { type: "text/csv" });
      const dl = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = dl;
      // Prefer server-suggested filename if present.
      const disposition = r.headers?.["content-disposition"] || "";
      const m = /filename="([^"]+)"/.exec(disposition);
      a.download = m ? m[1] : `quotes_export_${Date.now()}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(dl);
      toast.success("Export downloaded.");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message || "Export failed.");
    } finally {
      setExporting(false);
    }
  };

  const dateRangeInvalid =
    datePreset === "custom"
    && customStart && customEnd
    && customStart > customEnd;

  return (
    <div data-testid="quote-list">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
        <div>
          <div className="kicker kicker-cream">Pre-order Activity</div>
          <h2 className="font-display text-2xl md:text-3xl mt-1">Recent Quote Activity</h2>
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={handleExport}
            disabled={exporting || dateRangeInvalid}
            className="btn-ghost text-sm disabled:opacity-50"
            data-testid="quote-list-export"
            title={dateRangeInvalid ? "Fix custom dates first" : "Download CSV of matching quotes"}
          >
            {exporting ? <Loader2 size={14} className="animate-spin" /> : <Download size={14} />}
            {exporting ? "Exporting…" : "Export Quotes"}
          </button>
          <button
            type="button"
            onClick={load}
            className="btn-ghost text-sm"
            data-testid="quote-list-refresh"
          >
            Refresh
          </button>
        </div>
      </div>

      {/* Search + Sort */}
      <div className="grid grid-cols-1 md:grid-cols-[1fr_auto] gap-3 mb-3">
        <div className="relative">
          <input
            type="search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by ID, name, email, or phone…"
            data-testid="quote-search-input"
            className="w-full border-2 border-[var(--ink)] rounded-full px-4 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[var(--panini-yellow)]"
          />
          {search && (
            <button
              type="button"
              onClick={() => setSearch("")}
              data-testid="quote-search-clear"
              className="absolute right-3 top-1/2 -translate-y-1/2 text-[var(--smoke)] hover:text-[var(--ink)] text-xs font-bold"
              aria-label="Clear search"
            >
              ✕
            </button>
          )}
        </div>
        <select
          value={sortBy}
          onChange={(e) => setSortBy(e.target.value)}
          data-testid="quote-sort-select"
          className="border-2 border-[var(--ink)] rounded-full px-4 py-2 text-sm bg-white font-bold uppercase tracking-wider text-[11px]"
        >
          <option value="created_desc">Newest first</option>
          <option value="created_asc">Oldest first</option>
          <option value="last_activity_desc">Last activity</option>
          <option value="total_desc">Highest quote total</option>
          <option value="total_asc">Lowest quote total</option>
        </select>
      </div>

      {/* Q5.3 · Date filter + page size */}
      <div className="grid grid-cols-1 md:grid-cols-[auto_auto_1fr_auto] gap-3 items-center mb-3" data-testid="quote-date-filter">
        <select
          value={datePreset}
          onChange={(e) => setDatePreset(e.target.value)}
          data-testid="quote-date-preset"
          className="border-2 border-[var(--ink)] rounded-full px-4 py-2 text-sm bg-white font-bold uppercase tracking-wider text-[11px]"
        >
          {DATE_RANGE_OPTIONS.map((o) => (
            <option key={o.value} value={o.value}>{o.label}</option>
          ))}
        </select>
        {datePreset === "custom" && (
          <div className="flex gap-2 items-center" data-testid="quote-date-custom">
            <input
              type="date"
              value={customStart}
              onChange={(e) => setCustomStart(e.target.value)}
              data-testid="quote-date-start"
              className="border-2 border-[var(--ink)] rounded-full px-3 py-2 text-xs"
              aria-label="Start date"
            />
            <span className="text-xs">→</span>
            <input
              type="date"
              value={customEnd}
              onChange={(e) => setCustomEnd(e.target.value)}
              data-testid="quote-date-end"
              className="border-2 border-[var(--ink)] rounded-full px-3 py-2 text-xs"
              aria-label="End date"
            />
          </div>
        )}
        {datePreset !== "custom" && <div />}
        <div />
        <div className="flex items-center gap-2">
          <span className="text-[10px] uppercase tracking-widest font-bold text-[var(--smoke)]">Rows</span>
          <select
            value={pageSize}
            onChange={(e) => setPageSize(Number(e.target.value))}
            data-testid="quote-page-size"
            className="border-2 border-[var(--ink)] rounded-full px-3 py-1.5 text-xs bg-white font-bold"
          >
            {PAGE_SIZE_OPTIONS.map((n) => (
              <option key={n} value={n}>{n}</option>
            ))}
          </select>
        </div>
      </div>

      {dateRangeInvalid && (
        <div className="border-2 border-amber-800 bg-amber-50 text-amber-900 rounded-lg px-3 py-2 text-xs mb-3" data-testid="quote-date-invalid">
          Start date must be on or before the end date.
        </div>
      )}

      {/* Pipeline filter chips */}
      <div
        className="flex flex-wrap gap-2 mb-4"
        role="tablist"
        aria-label="Pipeline filter"
        data-testid="quote-pipeline-filter"
      >
        <FilterChip
          testid="quote-pipeline-filter-all"
          active={pipeline === null}
          onClick={() => setPipeline(null)}
        >All</FilterChip>
        {PIPELINE_FILTER_KEYS.map((key) => {
          const cfg = PIPELINE_LABEL[key];
          return (
            <FilterChip
              key={key}
              testid={`quote-pipeline-filter-${key}`}
              active={pipeline === key}
              onClick={() => setPipeline(key)}
            >
              {cfg.label}
            </FilterChip>
          );
        })}
      </div>

      {err && (
        <div className="border-2 border-red-800 bg-red-50 text-red-900 rounded-lg px-3 py-2 text-sm mb-4">
          {err}
        </div>
      )}

      <div className="border-2 border-[var(--ink)] rounded-2xl bg-white overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-[10px] uppercase tracking-widest border-b-2 border-[var(--ink)]">
              <th className="px-4 py-3 text-left">Quote ID</th>
              <th className="px-4 py-3 text-left whitespace-nowrap">Date · Time</th>
              <th className="px-4 py-3 text-left">Customer</th>
              <th className="px-4 py-3 text-left">Phone</th>
              <th className="px-4 py-3 text-right">Stickers</th>
              <th className="px-4 py-3 text-left">Variant</th>
              <th className="px-4 py-3 text-right">Quote Total</th>
              <th className="px-4 py-3 text-left">Pipeline</th>
              <th className="px-4 py-3 text-left">Status</th>
              <th className="px-4 py-3"></th>
            </tr>
          </thead>
          <tbody>
            {rows === null && (
              <tr><td colSpan={10} className="text-center py-8"><Loader2 className="animate-spin inline" size={18} /></td></tr>
            )}
            {rows && rows.length === 0 && (
              <tr><td colSpan={10} className="text-center py-10 text-[var(--smoke)]"
                      data-testid="quote-list-empty">
                {activeQuery
                  ? "No quotes match your search and filters."
                  : "No quote activity yet."}
              </td></tr>
            )}
            {rows && rows.map((q) => {
              const dt = queueTwoLine(q.created_at);
              const rowCls = q.admin_read
                ? "hover:bg-[var(--panini-yellow-soft)]/50"
                : "font-semibold hover:bg-[var(--panini-yellow-soft)]";
              return (
                <tr
                  key={q.public_id}
                  onClick={() => onOpen?.(q.public_id)}
                  className={`border-t border-neutral-200 cursor-pointer transition-colors ${rowCls}`}
                  data-testid={`quote-row-${q.public_id}`}
                >
                  <td className="px-4 py-3 font-mono">{q.public_id}</td>
                  <td className="px-4 py-3 whitespace-nowrap">
                    {dt ? (
                      <>
                        <div className="text-xs font-semibold">{dt.date}</div>
                        <div className="text-xs opacity-70">{dt.time}</div>
                      </>
                    ) : <span className="italic opacity-60">—</span>}
                  </td>
                  <td className="px-4 py-3">
                    {q.is_anonymous ? (
                      <span className="italic text-[var(--smoke)]" data-testid={`quote-anon-${q.public_id}`}>Anonymous</span>
                    ) : (
                      <>
                        <div data-testid={`quote-name-${q.public_id}`}>{q.customer_name || <span className="italic opacity-60">(no name)</span>}</div>
                        <div className="text-xs opacity-60 font-mono" data-testid={`quote-email-${q.public_id}`}>{q.customer_email || ""}</div>
                        {q.newsletter_opt_in && (
                          <span
                            className="mt-1 inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wide border border-emerald-800 bg-emerald-100 text-emerald-900 rounded px-1.5 py-0.5"
                            data-testid={`quote-newsletter-${q.public_id}`}
                          >
                            <CheckCircle2 size={9} /> Newsletter
                          </span>
                        )}
                      </>
                    )}
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap font-mono text-xs" data-testid={`quote-phone-${q.public_id}`}>
                    {q.customer_phone || <span className="italic opacity-60">—</span>}
                  </td>
                  <td className="px-4 py-3 text-right font-mono">{q.sticker_count ?? 0}</td>
                  <td className="px-4 py-3">
                    <VariantChip variant={q.variant} publicId={q.public_id} />
                  </td>
                  <td className="px-4 py-3 text-right font-mono">
                    {q.quote_total !== null && q.quote_total !== undefined
                      ? `$${Number(q.quote_total).toFixed(2)}`
                      : "—"}
                  </td>
                  <td
                    className="px-4 py-3"
                    data-testid={`quote-pipeline-cell-${q.public_id}`}
                  >
                    <PipelinePill pipeline={q.pipeline_status} />
                  </td>
                  <td className="px-4 py-3">
                    <StatusPill status={q.status} admin_read={q.admin_read} />
                    {q.converted_order_public_id && (
                      <a
                        href={`/admin/fulfillment/${q.converted_order_public_id}`}
                        onClick={(e) => e.stopPropagation()}
                        className="ml-1 text-[10px] mt-1 font-mono inline-flex items-center gap-1 border border-emerald-800 bg-emerald-100 text-emerald-900 rounded px-1.5 py-0.5 hover:bg-emerald-200 no-underline"
                        data-testid={`quote-list-order-link-${q.public_id}`}
                      >
                        <CheckCircle2 size={10} /> Order {q.converted_order_public_id}
                      </a>
                    )}
                  </td>
                  <td className="px-4 py-3 text-right">
                    <ChevronRight size={16} className="opacity-40 inline" />
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Q5.3 · Pagination controls */}
      <div
        className="flex flex-wrap items-center justify-between gap-3 mt-4"
        data-testid="quote-pagination"
      >
        <div className="text-xs text-[var(--smoke)]" data-testid="quote-pagination-summary">
          {total === 0
            ? "Showing 0 of 0 quotes"
            : `Showing ${showingStart.toLocaleString()}–${showingEnd.toLocaleString()} of ${total.toLocaleString()} quotes`}
        </div>
        <div className="flex items-center gap-1">
          <PaginationButton
            testid="quote-pagination-first"
            onClick={goFirst}
            disabled={offset === 0}
          >« First</PaginationButton>
          <PaginationButton
            testid="quote-pagination-prev"
            onClick={goPrev}
            disabled={offset === 0}
          >‹ Prev</PaginationButton>
          <span
            className="px-3 py-1.5 text-[11px] font-bold uppercase tracking-wider"
            data-testid="quote-pagination-current"
          >
            Page {currentPage} of {totalPages}
          </span>
          <PaginationButton
            testid="quote-pagination-next"
            onClick={goNext}
            disabled={offset + pageSize >= total}
          >Next ›</PaginationButton>
          <PaginationButton
            testid="quote-pagination-last"
            onClick={goLast}
            disabled={offset + pageSize >= total}
          >Last »</PaginationButton>
        </div>
      </div>
    </div>
  );
}


export { StatusPill, PipelinePill };


function FilterChip({ active, onClick, children, testid }) {
  return (
    <button
      type="button"
      role="tab"
      aria-selected={active}
      onClick={onClick}
      data-testid={testid}
      className={
        active
          ? "px-3 py-1 rounded-full border-2 border-[var(--ink)] bg-[var(--ink)] text-white text-[11px] font-bold uppercase tracking-wider"
          : "px-3 py-1 rounded-full border-2 border-[var(--ink)] bg-white text-[var(--ink)] text-[11px] font-bold uppercase tracking-wider hover:bg-[var(--panini-yellow-soft)]"
      }
    >
      {children}
    </button>
  );
}


function PaginationButton({ onClick, disabled, children, testid }) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      data-testid={testid}
      className="px-3 py-1.5 rounded-full border-2 border-[var(--ink)] bg-white text-[var(--ink)] text-[11px] font-bold uppercase tracking-wider hover:bg-[var(--panini-yellow-soft)] disabled:opacity-40 disabled:cursor-not-allowed"
    >
      {children}
    </button>
  );
}
